import clsx from 'clsx'
import { differenceInMinutes, format } from 'date-fns'
import type { TimeslotAvailability } from '../types/booking'

interface TimeslotSelectorProps {
  timeslots: TimeslotAvailability[]
  selectedTimeslotId: string | null
  onSelect: (timeslotId: string) => void
  selectedDate: Date | null
  loading?: boolean
  error?: string
  cutoffMinutes?: number
}

const DEFAULT_CUTOFF_MINUTES = 120

const TimeslotSelector = ({
  timeslots,
  selectedTimeslotId,
  onSelect,
  selectedDate,
  loading = false,
  error,
  cutoffMinutes = DEFAULT_CUTOFF_MINUTES,
}: TimeslotSelectorProps) => {
  const now = new Date()

  const renderContent = () => {
    if (!selectedDate) {
      return <p className="text-sm text-widget-accent/70">Select a date to reveal the available appointment times.</p>
    }

    if (loading) {
      return (
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((index) => (
            <div key={index} className="h-16 animate-pulse rounded-2xl bg-widget-primary/10" />
          ))}
        </div>
      )
    }

    if (error) {
      return (
        <p role="alert" className="rounded-2xl bg-red-500/10 px-4 py-3 text-sm text-red-200">
          {error}
        </p>
      )
    }

    if (!timeslots.length) {
      return <p className="text-sm text-widget-accent/70">No timeslots available for this date.</p>
    }

    return (
      <div className="grid gap-3 md:grid-cols-2">
        {timeslots.map((slot) => {
          const slotDate = getSlotDate(slot.startTime, selectedDate)
          const isCutOff =
            !slotDate || (isSameDayAsSelected(slotDate, selectedDate) && differenceInMinutes(slotDate, now) < cutoffMinutes)
          const isFull = slot.remainingSpots <= 0
          const disabled = isCutOff || isFull
          const selected = slot.id === selectedTimeslotId

          return (
            <button
              key={slot.id}
              type="button"
              disabled={disabled}
              onClick={() => onSelect(slot.id)}
              className={clsx(
                'relative flex h-20 flex-col justify-center rounded-xl border px-4 text-left transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-widget-secondary focus-visible:ring-offset-2 focus-visible:ring-offset-widget-primary',
                selected
                  ? 'border-widget-secondary bg-widget-secondary/20 text-widget-secondary shadow-lg scale-102'
                  : 'border-widget-primary/30 bg-widget-primary/15 text-widget-accent shadow-md hover:border-widget-secondary/70 hover:bg-widget-secondary/10 hover:text-widget-secondary hover:shadow-xl hover:scale-102',
                disabled && 'cursor-not-allowed opacity-40 hover:border-widget-primary/30 hover:bg-widget-primary/15 hover:shadow-md hover:scale-100',
              )}
            >
              <div className="flex items-center justify-between">
                <span className="font-display text-xl">
                  {formatSlotTime(slot.startTime, selectedDate)} – {formatSlotTime(slot.endTime, selectedDate)}
                </span>
                {!disabled && !isFull && (
                  <span className="flex items-center gap-1.5">
                    <span className="h-2 w-2 rounded-full bg-green-500"></span>
                  </span>
                )}
                {isFull && (
                  <span className="rounded-full bg-red-500 px-2 py-0.5 text-xs font-semibold text-white">
                    Telt ház
                  </span>
                )}
              </div>
              <span className="text-sm text-widget-accent/70">
                {slot.remainingSpots}/{slot.capacity} spots left
              </span>
              {isCutOff && (
              <span className="mt-1 text-xs uppercase tracking-[0.2em] text-widget-secondary/70">
                  Booking closes 2 hours before start
                </span>
              )}
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <section aria-labelledby="timeslot-heading" className="superz-widget-panel p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-widget-accent/70">Choose Your Moment</p>
          <h2 id="timeslot-heading" className="font-display text-3xl text-widget-accent">
            Timeslots
          </h2>
        </div>
      </div>
      {renderContent()}
    </section>
  )
}

const getSlotDate = (time: string, selectedDate: Date | null) => {
  if (time.includes('T')) return new Date(time)
  if (!selectedDate) return null
  const [hours, minutes] = time.split(':').map(Number)
  const date = new Date(selectedDate)
  date.setHours(hours ?? 0, minutes ?? 0, 0, 0)
  return date
}

const formatSlotTime = (time: string, selectedDate: Date | null) => {
  const date = getSlotDate(time, selectedDate)
  if (!date) return time
  return format(date, 'HH:mm')
}

const isSameDayAsSelected = (slotDate: Date, selectedDate: Date | null) => {
  if (!selectedDate) return false
  return (
    slotDate.getFullYear() === selectedDate.getFullYear() &&
    slotDate.getMonth() === selectedDate.getMonth() &&
    slotDate.getDate() === selectedDate.getDate()
  )
}

export default TimeslotSelector

